import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, GraduationCap, Globe2, Sun, Plane, Bus, UtensilsCrossed,
  ClipboardCheck, Headset, Sparkles, Calendar, Users, Star, MapPin, Heart, BookOpen
} from "lucide-react";
import TourCard from "@/components/TourCard";
import { fetchTours } from "@/lib/api";
import { HOME } from "@/constants/testIds";

const HBCUs = [
  "Morehouse", "Spelman", "Clark Atlanta", "Tuskegee",
  "Howard University", "Norfolk State University", "Hampton University",
];

const dreamDestinations = [
  "Jamaica", "St. Croix", "Turks and Caicos", "Thailand", "Mexico", "Africa",
  "Bali", "Niagara Falls", "New Orleans",
];

const featuredCities = ["New York City", "Miami", "Washington D.C."];

const collegePartners = [
  "Howard University", "Norfolk State University", "Hampton University",
  "Morehouse", "Spelman", "Clark Atlanta", "Tuskegee",
  "Xavier University of Louisiana", "Southern University", "Grambling State",
  "University of Michigan", "Michigan State", "Ohio State", "Notre Dame", "Northwestern",
  "UC Berkeley", "UCLA", "Stanford", "USC", "UC Davis",
  "University of Florida", "Florida State", "Florida Atlantic", "University of Miami", "FIU",
  "UConn", "Villanova", "Tennessee State",
];

const inclusives = [
  { icon: ClipboardCheck, t: "Tour coordinator", d: "Plans & customizes with you in advance." },
  { icon: Headset, t: "On-trip tour manager", d: "Travels with your group, every day." },
  { icon: Plane, t: "Round-trip air", d: "Group air arrangements, ticketed by us." },
  { icon: Bus, t: "Ground transportation", d: "Chartered motorcoach + airport transfers." },
  { icon: UtensilsCrossed, t: "All meals", d: "Breakfast, lunch & dinner — every day." },
  { icon: Sparkles, t: "All activities", d: "Every entry, tour & excursion included." },
];

const PROGRAMS = [
  { name: "Upward Bound", desc: "TRIO college-prep program for first-gen & low-income high schoolers." },
  { name: "Talent Search", desc: "TRIO Educational Talent Search — middle & high school college access." },
  { name: "AVID", desc: "Advancement Via Individual Determination — college readiness, grades 4–12." },
  { name: "GEAR UP", desc: "Gaining Early Awareness & Readiness for Undergraduate Programs." },
  { name: "TRIO SSS", desc: "Student Support Services — college persistence & success." },
  { name: "College Bound", desc: "Local, state & private college access programs nationwide." },
];

const heroStats = [
  { k: "25+", v: "Minimum group size" },
  { k: "12 yrs", v: "Designing group trips" },
  { k: "100%", v: "All-inclusive promise" },
];

export default function Home() {
  const [tours, setTours] = useState([]);

  useEffect(() => {
    fetchTours().then(setTours).catch(() => setTours([]));
  }, []);

  // Diverse spread: HBCU, Big Ten, Florida, plus our specialty regions (NYC + D.C.)
  const FEATURED_COLLEGE_SLUGS = [
    "atlanta-hbcu-circuit",
    "big-ten-michigan-tour",
    "florida-universities-tour",
    "new-york-city-college-tour",
    "washington-dc-college-tour",
    "east-coast-hbcu-tour",
  ];
  const collegeTours = FEATURED_COLLEGE_SLUGS
    .map((slug) => tours.find((t) => t.slug === slug))
    .filter(Boolean);
  const groupTours = tours.filter((t) => t.category === "Group Travel");

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-cream">
        {/* Vibrant warm background layers tuned to the logo palette */}
        <div className="absolute inset-0 bg-gradient-to-br from-terracotta via-terracottaDeep to-ink" />
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-gold/40 rounded-full blur-3xl animate-bounce-soft" />
        <div className="absolute bottom-[-15%] right-[-15%] w-[70%] h-[70%] bg-teal/45 rounded-full blur-3xl" />
        <div className="absolute top-[20%] right-[15%] w-32 h-32 bg-gold rounded-full blur-2xl opacity-70 animate-bounce-soft" />
        <div className="absolute inset-0 dotted-grid opacity-15" />

        {/* Floating decorative chips */}
        <div className="hidden md:block absolute top-32 right-16 bg-cream rounded-3xl px-5 py-3 shadow-xl tilt-r animate-bounce-soft">
          <div className="font-hand text-2xl text-terracotta">groups of 25+</div>
        </div>
        <div className="hidden md:block absolute bottom-32 right-32 bg-gold rounded-3xl px-5 py-3 shadow-xl tilt-l">
          <div className="font-hand text-2xl text-ink">all-inclusive ✦</div>
        </div>
        <div className="hidden lg:block absolute top-1/2 right-10 bg-teal text-cream rounded-3xl px-5 py-3 shadow-xl tilt-r">
          <div className="font-hand text-2xl">dream destinations</div>
        </div>
        <div className="hidden lg:block absolute bottom-44 left-1/2 bg-cream rounded-3xl px-5 py-3 shadow-xl tilt-l">
          <div className="font-hand text-2xl text-ink">HBCUs across the country</div>
        </div>

        <Sun size={56} strokeWidth={1.25} className="hidden md:block absolute top-32 left-1/2 text-gold animate-spin-slow opacity-90" />
        <Sparkles size={36} className="hidden md:block absolute bottom-1/3 left-16 text-gold animate-bounce-soft" />

        <div className="relative container-edge pb-24 pt-32 w-full z-10">
          <div className="max-w-4xl text-cream animate-fade-up">
            {/* Logo lockup */}
            <div className="inline-flex items-center gap-4 mb-7">
              <img
                src="/brand/logo.jpeg"
                alt="KNV Travelz logo"
                className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-cream p-1 shadow-2xl"
              />
              <div className="hidden sm:block">
                <div className="font-display font-semibold text-2xl text-cream leading-none">KNV Travelz</div>
                <div className="font-body text-[11px] uppercase tracking-[0.28em] text-gold mt-1">Group Tours</div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-cream/20 backdrop-blur-md text-cream rounded-full px-4 py-2 mb-6 border border-cream/20">
              <Sparkles size={14} className="text-gold" />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em]">For Groups of 25 or More</span>
            </div>
            <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
              Group Tours
              <span className="block">+ College Tours.</span>
              <span className="hand-accent text-gold text-6xl lg:text-8xl inline-block tilt-r mt-2">together.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-cream/90 leading-relaxed">
              We take <strong className="text-gold">college tours</strong> seriously. We take
              <strong className="text-gold"> group travel</strong> seriously. KNV Travelz designs custom
              trips for educational groups and any group of 25 or more dreaming of a getaway —
              and we love working with both.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/request-quote" className="btn-primary bg-ink text-cream hover:bg-cream hover:text-ink" data-testid={HOME.heroQuoteBtn}>
                Plan My Trip <ArrowRight size={16} />
              </Link>
              <Link to="/tours" className="btn-outline border-cream text-cream hover:bg-cream hover:text-ink" data-testid={HOME.heroExploreBtn}>
                Explore Tours
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-4 max-w-xl">
              {heroStats.map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl md:text-4xl text-cream">{s.k}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/75 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DREAM DESTINATIONS RIBBON */}
      <section className="bg-coral text-cream py-5 overflow-hidden border-y-4 border-ink">
        <div className="container-edge mb-3 flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/90">Dream Destinations</span>
          <span className="h-px flex-1 bg-cream/30" />
          <span className="font-hand text-2xl text-sun">we are experts in NYC + Miami ✦</span>
        </div>
        <div className="ribbon-track animate-marquee whitespace-nowrap font-display italic font-medium text-3xl">
          {[...Array(3)].flatMap((_, k) =>
            [...dreamDestinations.map((d) => ({ name: d, hi: false })),
              ...featuredCities.map((c) => ({ name: c, hi: true }))]
              .map((d, i) => (
                <span key={`${k}-${i}`} className="px-8 inline-flex items-center gap-8">
                  {d.hi ? (
                    <span className="inline-flex items-center gap-2 bg-sun text-ink rounded-full px-5 py-1.5 not-italic font-display font-semibold text-2xl">
                      <Star size={18} className="fill-ink" /> {d.name}
                    </span>
                  ) : (
                    <span>{d.name}</span>
                  )}
                  <span className="text-sun">✦</span>
                </span>
              ))
          )}
        </div>
      </section>

      {/* COLLEGE PARTNERS RIBBON */}
      <section className="bg-ink text-cream py-5 overflow-hidden border-b-4 border-ink">
        <div className="container-edge mb-3 flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-sun">College Tours · Partners</span>
          <span className="h-px flex-1 bg-cream/20" />
          <span className="font-hand text-2xl text-sun">campuses we love ✦</span>
        </div>
        <div className="ribbon-track animate-marquee whitespace-nowrap font-display italic font-medium text-3xl">
          {[...Array(3)].flatMap((_, k) =>
            collegePartners.map((p, i) => (
              <span key={`${k}-${i}`} className="px-8 inline-flex items-center gap-8 text-cream">
                {p} <span className="text-coral">✦</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* FEATURED THIS MONTH — auto-rotates by calendar month: 1 college + 1 group */}
      <section className="container-edge pt-24 pb-4" data-testid="home-featured-month">
        {(() => {
          const now = new Date();
          const monthName = now.toLocaleString("en-US", { month: "long" });
          const monthIdx = now.getMonth(); // 0-11
          // Pick one college tour + one group tour, rotating across the year
          const monthlyCollege = collegeTours.length
            ? collegeTours[monthIdx % collegeTours.length]
            : null;
          const monthlyGroup = groupTours.length
            ? groupTours[monthIdx % groupTours.length]
            : null;
          const featured = [monthlyCollege, monthlyGroup].filter(Boolean);
          if (featured.length < 2) return null;
          return (
            <>
              <div className="max-w-2xl mb-12">
                <div className="eyebrow mb-4" data-testid="featured-month-eyebrow">
                  <Calendar size={14} className="text-coral" /> Featured This {monthName}
                </div>
                <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
                  This month's <span className="hand-accent text-coral inline-block tilt-l">picks.</span>
                </h2>
                <p className="text-muted2 mt-4 leading-relaxed">
                  One college tour and one group trip we're highlighting this {monthName}. Fresh rotation every month — see what's catching our groups' attention right now.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl" data-testid="featured-month-grid">
                {featured.map((t, i) => (
                  <TourCard key={t.slug} tour={t} index={i} />
                ))}
              </div>
            </>
          );
        })()}
      </section>

      {/* TWO MESSAGES — COLLEGE TOURS & GROUP TRAVEL */}
      <section className="container-edge py-24 md:py-28" data-testid={HOME.offersSection}>
        <div className="max-w-2xl mb-16">
          <div className="eyebrow mb-4"><Star size={14} className="fill-coral" /> What We Do</div>
          <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
            Two things. Done right. <span className="hand-accent text-coral inline-block tilt-l">Every time.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* COLLEGE TOURS BLOCK */}
          <div className="bg-ink text-cream rounded-[2rem] p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-coral/30 rounded-full blur-3xl" />
            <GraduationCap size={48} strokeWidth={1.5} className="text-sun mb-6 relative" />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-sun mb-3">For Educators · 25+ Groups</div>
              <h3 className="display-serif text-4xl text-cream mb-4 leading-tight">College Tours</h3>
              <p className="text-cream/85 leading-relaxed mb-6">
                We visit both <strong className="text-gold">March Madness powerhouses</strong> and
                <strong className="text-gold"> historic HBCUs</strong> — we lift up both equally. From a single day to
                multi-day adventures, we mix and match campuses based on your group's shortlist. Tell us your
                top picks — we'll build the route.
              </p>

              <div className="mb-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-2.5">March Madness Powerhouses</div>
                <div className="flex flex-wrap gap-2">
                  {["Duke", "UConn", "Villanova", "Michigan", "UCLA", "Kansas", "Syracuse", "Florida State", "Kentucky", "Gonzaga"].map((h) => (
                    <span key={h} className="chip bg-cream/10 text-cream border border-cream/15">{h}</span>
                  ))}
                </div>
              </div>

              <div className="mb-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-2.5">Historic HBCUs</div>
                <div className="flex flex-wrap gap-2">
                  {["Morehouse", "Spelman", "Clark Atlanta", "Tuskegee", "Howard", "Hampton", "Norfolk State", "Xavier LA", "Southern", "Grambling"].map((h) => (
                    <span key={h} className="chip bg-cream/10 text-cream border border-cream/15">{h}</span>
                  ))}
                  <span className="chip bg-gold/30 text-cream border border-gold/40">+ many more</span>
                </div>
              </div>

              <ul className="space-y-2.5 mb-8 text-sm">
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold" /> Single-day to multi-day college tours</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold" /> <strong className="text-gold">Historic HBCUs</strong> — Morehouse, Spelman, Howard, Hampton + more</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold" /> <strong className="text-gold">March Madness powerhouses</strong> — Duke, UConn, Villanova, Michigan, UCLA</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold" /> Both groupings — equally lifted up on every tour</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold" /> <strong className="text-gold">Anywhere your group wants to go</strong> · fully custom</li>
              </ul>

              <Link to="/college-tours" className="btn-primary" data-testid={HOME.collegeTracksBtn}>
                See College Tours <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* GROUP TRAVEL BLOCK */}
          <div className="bg-teal text-cream rounded-[2rem] p-10 relative overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-sun/30 rounded-full blur-3xl" />
            <Globe2 size={48} strokeWidth={1.5} className="text-sun mb-6 relative" />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-sun mb-3">For Groups of 25+ · Dream Destinations</div>
              <h3 className="display-serif text-4xl text-cream mb-4 leading-tight">Group Travel</h3>
              <p className="text-cream/85 leading-relaxed mb-6">
                Got a group of 25 or more dreaming of a getaway? We design and run <strong className="text-gold">group
                trips to dream destinations</strong> — Thailand, Mexico, Africa, the Caribbean, NYC, Miami, beyond.
                Church groups, family reunions, alumni associations, social clubs — we build the trip,
                you bring the people.
              </p>

              <div className="mb-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/55 mb-3">Dream Destinations</div>
                <div className="flex flex-wrap gap-2">
                  {dreamDestinations.map((d) => (
                    <span key={d} className="chip bg-cream/10 text-cream border border-cream/15">{d}</span>
                  ))}
                  {featuredCities.map((c) => (
                    <span key={c} className="chip bg-sun text-ink border border-sun font-semibold">★ {c}</span>
                  ))}
                  <span className="chip bg-cream/10 text-cream border border-cream/15">+ anywhere you dream</span>
                </div>
              </div>

              <ul className="space-y-2.5 mb-8 text-sm">
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-sun" /> International & domestic group trips</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-sun" /> All-inclusive resort takeovers</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-sun" /> Cultural, civics & heritage tours</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-sun" /> Custom-built for your group's vibe</li>
              </ul>

              <Link to="/group-travel" className="btn-primary" data-testid={HOME.groupTravelBtn}>
                See Group Trips <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ALL-INCLUSIVE PROMISE */}
      <section className="bg-cream py-20 md:py-24 relative">
        <div className="container-edge">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="eyebrow justify-center mb-4"><Heart size={14} className="fill-coral" /> The KNV Promise</div>
            <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
              Every trip. <span className="hand-accent text-coral inline-block tilt-r">Fully handled.</span>
            </h2>
            <p className="mt-5 text-muted2 leading-relaxed">
              Each KNV tour comes with a dedicated <strong>tour coordinator</strong> in advance and a
              separate <strong>tour manager</strong> on the road. Every item below is standard on every trip.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {inclusives.map(({ icon: Icon, t, d }, i) => (
              <div key={t} className={`bg-white rounded-3xl p-6 ring-1 ring-ink/8 hover:soft-shadow transition-all ${i % 2 === 0 ? "" : "md:translate-y-6"}`}>
                <div className="w-12 h-12 rounded-2xl bg-coral/10 flex items-center justify-center mb-4">
                  <Icon size={22} strokeWidth={1.75} className="text-coral" />
                </div>
                <h3 className="display-serif text-xl mb-1.5">{t}</h3>
                <p className="text-sm text-muted2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED COLLEGE TOURS */}
      <section className="container-edge py-24" data-testid={HOME.featuredSection}>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <div className="eyebrow mb-4"><GraduationCap size={14} /> Featured · College Tours</div>
            <h2 className="display-serif text-4xl sm:text-5xl leading-tight">
              HBCU · Big Ten · Florida — <span className="hand-accent text-terracotta">NYC &amp; D.C. our specialty</span>.
            </h2>
            <p className="mt-4 text-muted2 leading-relaxed">
              We design college tours across the country — historic HBCUs, Big Ten powerhouses, Florida schools,
              and our two specialty regions: New York City and Washington, D.C. We can do schools anywhere.
            </p>
          </div>
          <Link to="/college-tours" className="btn-ghost">
            All college tours <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collegeTours.map((tour, i) => (
            <TourCard key={tour.slug} tour={tour} index={i} />
          ))}
        </div>

        {/* Customize Anywhere callout */}
        <div className="mt-10 bg-coral text-cream rounded-[2rem] p-8 md:p-10 grid grid-cols-12 gap-6 items-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-sun/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-ink/30 rounded-full blur-3xl" />
          <div className="col-span-12 md:col-span-8 relative">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sun mb-4">Don't see your school?</div>
            <h3 className="display-serif text-3xl md:text-4xl leading-tight">
              We customize tours to fit <span className="hand-accent text-sun text-5xl inline-block tilt-r">anywhere</span> your group wishes to travel.
            </h3>
            <p className="mt-4 text-cream/90 max-w-xl">
              Tell us the schools, the cities, or the experience you're after — we build the entire trip
              around your group's goals. Domestic, international, single-state, or coast-to-coast.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end relative">
            <Link to="/request-quote" className="btn-primary bg-ink text-cream hover:bg-cream hover:text-ink">
              Request a Custom Tour <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED GROUP TRAVEL */}
      <section className="bg-teal/8 py-24">
        <div className="container-edge">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <div className="eyebrow mb-4"><Globe2 size={14} /> Featured · Group Travel</div>
              <h2 className="display-serif text-4xl sm:text-5xl leading-tight">
                Dream destinations, <span className="hand-accent text-teal inline-block tilt-l">handled.</span>
              </h2>
            </div>
            <Link to="/group-travel" className="btn-ghost">
              All group trips <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupTours.slice(0, 3).map((tour, i) => (
              <TourCard key={tour.slug} tour={tour} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* NYC SPOTLIGHT */}
      <section className="container-edge py-24" data-testid="home-nyc-spotlight">
        <div className="relative bg-ink text-cream rounded-[2.5rem] p-10 md:p-16 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] bg-coral/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] bg-sun/25 rounded-full blur-3xl" />
          <div className="absolute top-12 right-12 hidden lg:flex items-center gap-2 bg-sun text-ink rounded-full px-5 py-2 font-display font-bold text-sm tilt-r">
            <Star size={14} className="fill-ink" /> #1 DESTINATION
          </div>

          <div className="relative grid grid-cols-12 gap-10 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sun mb-4">
                Our Specialty · Our Expertise
              </div>
              <h2 className="display-serif text-cream text-5xl sm:text-6xl lg:text-[5rem] leading-[0.95] tracking-tight">
                New York City.
                <span className="block hand-accent text-sun text-5xl lg:text-7xl mt-3 tilt-l">our home turf.</span>
              </h2>
              <p className="mt-7 max-w-2xl text-cream/85 leading-relaxed text-base md:text-lg">
                NYC is our <strong className="text-sun">most-traveled destination</strong> — period. We design
                <strong className="text-sun"> cultural and specialty tours</strong> in NYC unlike anyone else:
                Harlem with local historians, Broadway, hip-hop heritage, food tours through every borough,
                fashion days in SoHo, the Met, the Apollo, and everything in between. Tell us your group's
                vibe — we'll build the NYC trip around it.
              </p>

              <div className="mt-8 flex flex-wrap gap-2">
                {[
                  "Cultural Tours", "Specialty Tours", "Harlem History", "Broadway",
                  "Hip-Hop Heritage", "Food Tours", "Fashion & Shopping",
                  "Museums (Met, MoMA, Whitney)", "Statue of Liberty", "Top of the Rock",
                ].map((tag, i) => (
                  <span key={tag} className={`chip ${i % 2 === 0 ? "bg-coral text-cream" : "bg-cream/15 text-cream border border-cream/20"}`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="col-span-12 lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <Link to="/tours/new-york-city-experience" className="btn-primary w-full lg:w-auto bg-coral hover:bg-coralDeep">
                See NYC Tour <ArrowRight size={16} />
              </Link>
              <Link to="/request-quote" className="btn-outline w-full lg:w-auto border-cream text-cream hover:bg-cream hover:text-ink">
                Plan My NYC Trip
              </Link>
              <div className="mt-4 font-hand text-2xl text-sun text-center lg:text-right">
                Miami expertise too ✦
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS WE LOVE HOSTING */}
      <section className="container-edge py-24" data-testid="home-programs-section">
        <div className="grid grid-cols-12 gap-10 mb-12">
          <div className="col-span-12 md:col-span-6">
            <div className="eyebrow mb-3"><Heart size={14} className="fill-coral" /> Programs We Love Hosting</div>
            <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
              We <span className="hand-accent text-coral inline-block tilt-l">love</span> hosting
              Upward Bound, Talent Search &amp; AVID groups.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 flex items-end">
            <p className="text-muted2 leading-relaxed">
              We work hand-in-hand with <strong className="text-ink">Upward Bound</strong>,
              <strong className="text-ink"> Talent Search</strong>,
              <strong className="text-ink"> AVID</strong>, <strong className="text-ink">GEAR UP</strong>,
              and other college-access programs across the country. Every itinerary gets
              <strong className="text-ink"> curated to the specific needs of your program and your students</strong> —
              grade levels, learning goals, exposure priorities, and the colleges that matter most to the families you serve.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROGRAMS.map((p, i) => (
            <div
              key={p.name}
              className={`relative bg-white rounded-3xl p-7 ring-1 ring-ink/8 hover:soft-shadow hover:-translate-y-1 transition-all ${i % 2 === 0 ? "" : "md:translate-y-4"}`}
            >
              <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-coral text-cream flex items-center justify-center">
                <BookOpen size={18} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-coralDeep mb-2">
                Program {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="display-serif text-2xl mb-2">{p.name}</h3>
              <p className="text-sm text-muted2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-sun/25 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-4">
            <Sparkles size={32} className="text-coral shrink-0" />
            <p className="font-display text-xl md:text-2xl text-ink leading-snug max-w-2xl">
              Your students. Your goals. <span className="hand-accent text-coral text-3xl">Your tour.</span>
              <br className="hidden md:block" />
              <span className="text-base font-body text-muted2 font-normal">
                Don't see your program above? We curate trips for any college-access organization. Tell us what your group needs.
              </span>
            </p>
          </div>
          <Link to="/request-quote" className="btn-primary shrink-0">
            Start a Tour <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-edge py-24" data-testid="home-how-it-works">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow mb-3"><Sparkles size={14} className="fill-terracotta" /> How It Works</div>
          <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
            From inquiry to <span className="hand-accent text-terracotta">unforgettable</span> in four steps.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { n: "01", t: "Tell us about your group", d: "Fill out the quote form — group size, destination interests, dates, and any program needs." },
            { n: "02", t: "We design your trip", d: "Your dedicated tour coordinator builds a custom itinerary tailored to your students or travelers." },
            { n: "03", t: "We finalize together", d: "We refine pacing, lodging, meals, and add-ons until the trip fits your group perfectly." },
            { n: "04", t: "We travel with you", d: "A KNV tour manager joins your group on the road, handling every logistic so you can be present." },
          ].map((s, i) => (
            <div key={s.n} className={`bg-white rounded-3xl p-7 ring-1 ring-ink/8 hover:soft-shadow transition-all ${i % 2 === 1 ? "md:translate-y-6" : ""}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-terracottaDeep mb-3">Step {s.n}</div>
              <h3 className="display-serif text-2xl mb-2">{s.t}</h3>
              <p className="text-sm text-muted2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-sage/40 py-24" data-testid={HOME.testimonialSection}>
        <div className="container-edge">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3"><Star size={14} className="fill-terracotta" /> Leader Notes</div>
            <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
              Real groups. <span className="hand-accent text-terracotta">Real reviews.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "Nicole was our guide and she was very professional, knowledgeable and had an upbeat personality. She took us to all the attractions we requested — aside from Times Square, 5th Ave was the cherry on top. We have another trip to Florida summer 2026 and we'll definitely be using them again. Thanks Nicole!",
                name: "Ms. Terry", role: "NYC family tour · visiting from South Carolina · ★★★★★ Google",
                initial: "T", color: "bg-terracotta",
              },
              {
                quote: "From start to finish, they made planning my trip completely stress-free. They took the time to understand what I wanted, offered great options within my budget, and handled every little detail with care. I'll definitely be booking all my future trips through KNV Travelz — highly recommend!",
                name: "Dara C.", role: "Custom group trip · ★★★★★ Google Review",
                initial: "D", color: "bg-teal",
              },
              {
                quote: "I traveled with my fitness group of 30 from New Jersey to Belize for a training retreat, and KNV handled everything from start to finish. Having a Tour Manager travel with us from the moment we left the U.S. until we got back made the whole experience feel VIP — they even got us extra perks at the resort. We'll definitely be booking our next trip with KNV.",
                name: "Eric S.", role: "Fitness group of 30 · NJ → Belize retreat · ★★★★★ Google",
                initial: "E", color: "bg-gold text-ink",
              },
            ].map((t) => (
              <div key={t.name} className="bg-white rounded-3xl p-8 ring-1 ring-ink/8 hover:soft-shadow transition-all">
                <div className="text-terracotta text-5xl font-display leading-none mb-4">"</div>
                <p className="text-ink/90 leading-relaxed mb-6">{t.quote}</p>
                <div className="flex items-center gap-3 pt-5 border-t border-ink/10">
                  <div className={`w-11 h-11 rounded-full ${t.color} flex items-center justify-center font-display text-lg text-cream`}>
                    {t.initial}
                  </div>
                  <div>
                    <div className="font-semibold text-ink text-sm">{t.name}</div>
                    <div className="text-xs text-muted2">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-edge py-24" data-testid="home-faq">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="eyebrow mb-3"><Sparkles size={14} className="fill-terracotta" /> FAQ</div>
            <h2 className="display-serif text-4xl sm:text-5xl leading-[1.05]">
              The most-asked <span className="hand-accent text-terracotta">questions</span>.
            </h2>
            <p className="mt-5 text-muted2 leading-relaxed">
              Still unsure? The quote form is a no-pressure way to start the conversation. We'll respond
              within two business days with answers tailored to your group.
            </p>
            <Link to="/request-quote" className="btn-primary mt-7">
              Plan My Trip <ArrowRight size={16} />
            </Link>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-3">
            {[
              {
                q: "Can we book online?",
                a: "No — every KNV trip is custom-built, so we don't sell tours through a booking engine. You'll work directly with a real tour coordinator from the first call to the last day on the road.",
              },
              {
                q: "What's the minimum group size?",
                a: "Twenty-five travelers. Every itinerary, price, and logistic is built for groups of 25 or more — that's how we deliver the all-inclusive experience.",
              },
              {
                q: "What does 'all-inclusive' actually include?",
                a: "Round-trip group air, all ground transportation, lodging, every meal, all activities/admissions/entries, plus a dedicated tour coordinator before the trip and an on-trip tour manager during. Nothing surprise-billed.",
              },
              {
                q: "Can you customize a trip to schools or destinations not listed?",
                a: "Absolutely. Our featured tours are starting points — we'll adjust schools, cities, pacing, lodging tier, and add-ons to fit your group's exact goals. We can take groups anywhere in the country (and internationally).",
              },
              {
                q: "How far in advance should we book?",
                a: "Most groups inquire 4–9 months out, especially for HBCU tours, Niagara Falls, New Orleans, and international trips. We've also pulled off shorter timelines — just call us.",
              },
              {
                q: "Do you work with Upward Bound, AVID, Talent Search and other programs?",
                a: "Yes — we love working with college-access programs. Every itinerary gets curated to your program's specific student needs and learning goals.",
              },
            ].map((f) => (
              <details key={f.q} className="bg-white rounded-2xl ring-1 ring-ink/8 px-6 py-4 group">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display text-lg text-ink group-open:text-terracotta transition-colors">
                  <span>{f.q}</span>
                  <span className="text-terracotta text-2xl leading-none w-6 h-6 flex items-center justify-center transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="container-edge pb-24" data-testid={HOME.ctaBanner}>
        <div className="bg-ink text-cream rounded-[2.5rem] p-12 md:p-20 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-coral/30 rounded-full blur-3xl animate-bounce-soft" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-teal/40 rounded-full blur-3xl" />
          <div className="absolute top-10 right-12 hidden lg:block">
            <Sun size={64} strokeWidth={1.25} className="text-sun animate-spin-slow" />
          </div>
          <div className="relative grid grid-cols-12 gap-10 items-center">
            <div className="col-span-12 md:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sun mb-5">Let's go</div>
              <h2 className="display-serif text-cream text-4xl sm:text-5xl leading-tight">
                Got a group of 25 or more? <br />
                <span className="hand-accent text-sun inline-block tilt-r">Tell us where you wanna go.</span>
              </h2>
              <p className="mt-5 text-cream/80 max-w-xl">
                We don't take bookings online — every trip starts with a conversation. We'll be back within two
                business days with a custom proposal and a real human to talk to.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end">
              <Link to="/request-quote" className="btn-primary">
                Plan My Trip <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
